import { useEffect, useRef, useState } from 'react';
import { useVektorStore } from './lib/use-vektor-store';
import { DropZone } from './components/DropZone';
import { Viewer } from './components/Viewer';
import { ControlsPanel } from './components/Controls/ControlsPanel';
import { StickyBar } from './components/StickyBar';
import { MobileBottomSheet } from './components/MobileBottomSheet';

function Feature({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="ticked rounded border border-[var(--border)] bg-[var(--entry)] p-4">
      <div className="mb-1 font-medium text-[var(--primary)]">{title}</div>
      <div className="text-[var(--secondary)]">{children}</div>
    </div>
  );
}

export default function App() {
  const store = useVektorStore();
  const [beforeUrl, setBeforeUrl] = useState<string | null>(null);
  const beforeUrlRef = useRef<string | null>(null);

  useEffect(() => () => {
    if (beforeUrlRef.current) URL.revokeObjectURL(beforeUrlRef.current);
  }, []);

  // The workspace view fills the viewport below the sticky nav. Lock body
  // scroll while it's mounted so a tiny height mismatch (nav height vs.
  // the 65px assumption below) doesn't introduce a second scrollbar next
  // to the sidebar's internal one.
  useEffect(() => {
    if (!store.sourceLoaded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [store.sourceLoaded]);

  const onFile = async (f: File) => {
    if (beforeUrlRef.current) URL.revokeObjectURL(beforeUrlRef.current);
    const url = URL.createObjectURL(f);
    beforeUrlRef.current = url;
    setBeforeUrl(url);
    await store.loadFile(f);
  };

  const onClear = () => {
    if (beforeUrlRef.current) URL.revokeObjectURL(beforeUrlRef.current);
    beforeUrlRef.current = null;
    setBeforeUrl(null);
    store.clearFile();
  };

  if (!store.sourceLoaded) {
    return (
      <main className="layer-lines mx-auto flex max-w-[1100px] flex-col gap-8 p-6">
        <header className="flex flex-col items-center gap-4 pt-4 text-center sm:pt-10">
          <span className="tech-label">Image → SVG Tracer</span>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">
            Turn any image into a clean vector
          </h1>
          <span className="accent-rule" />
          <p className="mx-auto max-w-2xl text-base text-[var(--secondary)] sm:text-lg">
            Vektor traces raster images into crisp vector SVGs — black-and-white
            with Potrace-style settings or full-color via VTracer. Drop a file
            and tune ~30 parameters live: thresholding, speckle filtering,
            curve smoothing, posterisation, post-processing with SVGO, and
            export.
          </p>
        </header>

        <DropZone onFile={onFile} empty />

        <section className="grid gap-4 text-sm text-[var(--secondary)] sm:grid-cols-3">
          <Feature title="B&W & Color modes">
            Binary tracing for logos, icons, and line art; color tracing for
            photos and illustrations with layered or cut-out hierarchy.
          </Feature>
          <Feature title="Live preview">
            Side-by-side or slider comparison against the source, with
            zoom-to-fit, 100%, and 200% views.
          </Feature>
          <Feature title="Private & offline">
            Everything runs locally in a Web Worker. No upload, no server, no
            tracking of your images.
          </Feature>
        </section>

        <p className="text-center text-xs text-[var(--secondary)]">
          Supports PNG, JPG, WebP, AVIF, GIF, BMP, SVG, HEIC, and PDF input.
        </p>
      </main>
    );
  }

  const controls = <ControlsPanel store={store} />;

  return (
    <div className="flex h-[calc(100dvh-65px)] flex-col">
      <DropZone onFile={onFile} empty={false} />
      <div className="flex flex-1 overflow-hidden">
        <Viewer beforeSrc={beforeUrl} afterSvg={store.svg} isProcessing={store.isProcessing} />
        <div className="hidden h-full sm:block">{controls}</div>
      </div>
      <MobileBottomSheet>{controls}</MobileBottomSheet>
      <StickyBar metrics={store.metrics} svg={store.svg} isProcessing={store.isProcessing} onClear={onClear} />
    </div>
  );
}
